// src/pages/Branches.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Trophy, 
  Building2,
  Map,
  Search,
  Filter,
  Users,
  Award,
  Globe
} from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { BranchCard } from '@/components/BranchCard';
import { branches } from '@/data/mock/branches';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const Branches = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');

  // Filter branches
  const filteredBranches = branches.filter(branch => {
    const matchesSearch = branch.name[isRTL ? 'ar' : 'en']
      .toLowerCase()
      .includes(searchQuery.toLowerCase()) ||
      branch.address[isRTL ? 'ar' : 'en']
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || branch.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  // Regions for filtering
  const regions = [
    { id: 'all', label: { en: 'All Branches', ar: 'جميع الفروع' } },
    { id: 'north', label: { en: 'North', ar: 'الشمال' } },
    { id: 'central', label: { en: 'Central', ar: 'الوسط' } },
    { id: 'south', label: { en: 'South', ar: 'الجنوب' } },
  ];

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={t('branches.title')}
        subtitle={t('branches.subtitle')}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.branches') }
        ]}
      />

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            {[
              { 
                icon: Building2, 
                value: branches.length.toString(), 
                label: { en: 'Branches', ar: 'فرع' } 
              },
              { 
                icon: Trophy, 
                value: '50+', 
                label: { en: 'Sports Programs', ar: 'برنامج رياضي' } 
              },
              { 
                icon: Users, 
                value: '10K+', 
                label: { en: 'Active Members', ar: 'عضو نشط' } 
              },
              { 
                icon: Award, 
                value: '15+', 
                label: { en: 'Countries', ar: 'دولة' } 
              },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="p-6 rounded-2xl bg-card/80 backdrop-blur-xl border border-border/50 hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <stat.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            {/* Search Bar */}
            <div className="relative mb-8">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? "ابحث عن فرع..." : "Search for a branch..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-12 text-right' : 'pl-12'} h-14 text-lg rounded-full border-2 focus:border-primary/50`}
              />
            </div>

            {/* Region Filter */}
            <div className="flex flex-wrap gap-3 justify-center">
              {regions.map((region) => (
                <Button
                  key={region.id}
                  variant={selectedRegion === region.id ? "default" : "outline"}
                  size="lg"
                  onClick={() => setSelectedRegion(region.id)}
                  className="rounded-full px-6"
                >
                  <MapPin className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {region.label[isRTL ? 'ar' : 'en']}
                </Button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Branches Grid */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          {/* Results Count */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-muted-foreground">
              {isRTL 
                ? `تم العثور على ${filteredBranches.length} فرع`
                : `Found ${filteredBranches.length} branches`
              }
            </p>
          </motion.div>

          {/* Branches Grid */}
          {filteredBranches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBranches.map((branch, idx) => (
                <BranchCard
                  key={branch.id}
                  slug={branch.slug}
                  name={branch.name[isRTL ? 'ar' : 'en']}
                  address={branch.address[isRTL ? 'ar' : 'en']}
                  phone={branch.phone}
                  image={branch.image}
                  availableSports={branch.availableSports.length}
                  isMainBranch={branch.isMainBranch}
                  delay={idx * 0.1}
                />
              ))}
            </div>
          ) : (
            // No Results
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
                <Search className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL 
                  ? 'جرب البحث بكلمات مختلفة أو تصفح جميع الفروع'
                  : 'Try different keywords or browse all branches'
                }
              </p>
              <Button onClick={() => { setSearchQuery(''); setSelectedRegion('all'); }}>
                {isRTL ? 'إعادة تعيين البحث' : 'Reset Search'}
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Map className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'موقعنا' : 'Our Locations'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'اعثر على أقرب فرع إليك' : 'Find the Nearest Branch'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'نحن منتشرون في جميع أنحاء فلسطين لخدمتك بشكل أفضل'
                : 'We are spread across Palestine to serve you better'
              }
            </p>
          </motion.div>

          {/* Map Placeholder - يمكن استبداله بـ Google Maps */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="aspect-video max-w-5xl mx-auto rounded-3xl overflow-hidden border-2 border-border shadow-2xl"
          >
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <div className="text-center">
                <Map className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {isRTL ? 'خريطة تفاعلية قريباً' : 'Interactive Map Coming Soon'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Globe className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'زرنا اليوم' : 'Visit Us Today'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'جاهز للبدء؟' : 'Ready to Start?'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'قم بزيارة أقرب فرع لنا واحصل على جولة مجانية في مرافقنا'
                : 'Visit our nearest branch and get a free tour of our facilities'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
                <Phone className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'اتصل بنا' : 'Contact Us'}
              </Button>
              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <MapPin className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'احصل على الاتجاهات' : 'Get Directions'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Branches;